import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { cn } from '~/utils/cn';
import { formatRelative } from '~/utils/date';

import type { EnrichmentValidationProgress } from '../../types/enrichment-validation.api.types';
import {
  progressFigures,
  roundRemaining,
  type ProgressFigures,
} from '../../utils/validation-progress';

interface Props {
  /** The RUNNING run's progress; `null` before the run has recorded any (it was just started,
   *  or the integration service predates progress). */
  progress: EnrichmentValidationProgress | null;
  /** When the run started (the summary's `startedAt`). */
  startedAt: string | null;
}

/** The RUNNING banner body: stage, a real progress bar over the run's expected products, what
 *  has been found so far, and roughly how long is left. Without a total it shows activity only —
 *  never a percentage it cannot back. */
export function ValidationRunProgress({ progress, startedAt }: Props) {
  const { t } = useTranslation('catalog-operations');
  const figures = progress ? progressFigures(progress, startedAt) : null;
  const stage = progress?.stage ?? 'PREPARING';
  const total = progress?.expectedProducts ?? null;
  const percent = figures ? figures.percent : null;

  const checkedLine =
    progress && figures
      ? total !== null
        ? t('enrichmentValidation.banner.running.checkedOf', {
            checked: figures.checked.toLocaleString(),
            total: total.toLocaleString(),
          })
        : progress.checkedProducts > 0
          ? t('enrichmentValidation.banner.running.checkedSoFar', {
              count: progress.checkedProducts,
              formatted: progress.checkedProducts.toLocaleString(),
            })
          : null
      : null;

  return (
    <div data-testid="batch-detail-validation-progress" className="space-y-3">
      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between gap-3 text-xs">
          <span
            data-testid="batch-detail-validation-progress-stage"
            className="text-foreground font-medium"
          >
            {t(`enrichmentValidation.banner.running.stage.${stage}`)}
          </span>
          {percent !== null ? (
            <span
              data-testid="batch-detail-validation-progress-percent"
              className="text-foreground shrink-0 font-semibold tabular-nums"
            >
              {t('enrichmentValidation.banner.running.percent', { percent })}
            </span>
          ) : null}
        </div>
        <ProgressBar
          percent={stage === 'FINISHING' ? 100 : percent}
          label={checkedLine ?? t(`enrichmentValidation.banner.running.stage.${stage}`)}
        />
        {checkedLine ? (
          <p
            data-testid="batch-detail-validation-progress-checked"
            className="text-muted-foreground text-xs tabular-nums"
          >
            {checkedLine}
          </p>
        ) : null}
      </div>

      {progress && progress.checkedProducts > 0 ? (
        <dl className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Figure
            testId="batch-detail-validation-progress-invalid-value"
            label={t('enrichmentValidation.banner.running.needsNormalisingSoFar')}
            value={progress.invalidProducts.toLocaleString()}
            emphasis={progress.invalidProducts > 0}
          />
          <Figure
            testId="batch-detail-validation-progress-warnings-value"
            label={t('enrichmentValidation.banner.running.warningsSoFar')}
            value={progress.warningProducts.toLocaleString()}
          />
          <Figure
            testId="batch-detail-validation-progress-time-left-value"
            label={t('enrichmentValidation.banner.running.timeLeft.label')}
            value={timeLeftText(figures, stage, t)}
          />
        </dl>
      ) : null}

      <p className="text-muted-foreground flex flex-wrap gap-x-2 text-xs">
        {startedAt ? (
          <span>
            {t('enrichmentValidation.banner.running.started', { time: formatRelative(startedAt) })}
          </span>
        ) : null}
        {startedAt && progress ? <span aria-hidden="true">·</span> : null}
        {progress ? (
          <span>
            {t('enrichmentValidation.banner.running.updated', {
              time: formatRelative(progress.updatedAt),
            })}
          </span>
        ) : null}
      </p>
    </div>
  );
}

/** Determinate when the share is known, else an activity pulse. One `progressbar` either way. */
function ProgressBar({ percent, label }: { percent: number | null; label: string }) {
  return (
    <div
      role="progressbar"
      data-testid="batch-detail-validation-progress-bar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      {...(percent !== null ? { 'aria-valuenow': percent } : {})}
      className="bg-muted h-2 w-full overflow-hidden rounded-full"
    >
      {percent !== null ? (
        <div
          className="bg-primary h-full rounded-full transition-[width] duration-500"
          // At least a sliver once anything is checked, so 0 % still reads as started.
          style={{ width: `${Math.max(percent, 1).toString()}%` }}
        />
      ) : (
        <div className="bg-primary/70 h-full w-1/3 animate-pulse rounded-full" />
      )}
    </div>
  );
}

function Figure({
  label,
  value,
  emphasis = false,
  testId,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  /** Automation hook for the figure's value cell. */
  testId: string;
}) {
  return (
    <div className="bg-muted/30 rounded-lg border px-3 py-2">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd
        data-testid={testId}
        className={cn(
          'text-sm font-semibold tabular-nums',
          emphasis ? 'text-primary' : 'text-foreground',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function timeLeftText(
  figures: ProgressFigures | null,
  stage: EnrichmentValidationProgress['stage'],
  t: TFunction<'catalog-operations'>,
): string {
  if (stage === 'FINISHING') return t('enrichmentValidation.banner.running.timeLeft.finishing');
  const remainingMs = figures ? figures.remainingMs : null;
  if (remainingMs === null) return t('enrichmentValidation.banner.running.timeLeft.unknown');
  const left = roundRemaining(remainingMs);
  if (left.unit === 'underMinute')
    return t('enrichmentValidation.banner.running.timeLeft.underMinute');
  if (left.unit === 'minutes') {
    return t('enrichmentValidation.banner.running.timeLeft.minutes', { minutes: left.minutes });
  }
  return t('enrichmentValidation.banner.running.timeLeft.hours', {
    hours: left.hours,
    minutes: left.minutes,
  });
}
